"use client";

import { useState } from "react";
import Button from "../components/Button";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { useRouter } from "next/navigation";
import { trackRegistrationConversion } from "../lib/gtag";
import { PLANS, type BillablePlan } from "../lib/billingPlans";

/** Only plans that map to a real Stripe price can be signed up for. */
const SELECTABLE_PLANS = PLANS.filter((p) => p.available);

/** Field labels, bold so the eye can run down the form by name. */
const labelStyle: React.CSSProperties = { fontSize: 14, fontWeight: 600 };

/** Every field wrapper was repeating this, with 40px of gap between rows. */
const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
  marginBottom: 16,
};

/** Two fields on one row — used for name and for the password pair. */
const fieldRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};

export default function RegistrationForm({
  initialPlan,
}: {
  /** Preselected from `?plan=` — the marketing site links straight to a plan. */
  initialPlan: BillablePlan;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  // Kept mounted but hidden when closed, so the field and its validation state
  // survive a toggle and the existing tests still find the input.
  const [showDiscountCode, setShowDiscountCode] = useState(false);
  // Whoever registers is always the clinic's admin; this only decides whether
  // they also hold a schedule and see patients. Defaults to true — the single
  // doctor registering their own practice is the common case.
  const [isProvider, setIsProvider] = useState(true);
  // Billed monthly to start with — the cycle can be switched in Platby, and the
  // trial means nothing is charged either way until it ends.
  const [plan, setPlan] = useState<BillablePlan>(initialPlan);
  const [discountCode, setDiscountCode] = useState("");
  const [discountCodeStatus, setDiscountCodeStatus] = useState<
    null | "valid" | "invalid"
  >(null);

  const VALID_COUPON = "kongres2026";

  function handleDiscountCodeChange(value: string) {
    setDiscountCode(value);
    if (value.trim() === "") {
      setDiscountCodeStatus(null);
    } else if (value.trim().toLowerCase() === VALID_COUPON) {
      setDiscountCodeStatus("valid");
    } else {
      setDiscountCodeStatus("invalid");
    }
  }

  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const supabase = createClientComponentClient();

  async function registrationHandler() {
    if (
      !email ||
      !password ||
      !confirmPassword ||
      !clinicName ||
      !firstName ||
      !lastName ||
      !termsChecked
    ) {
      setError("Vyplňte všetky polia a udeľte súhlas s podmienkami");
      return;
    }

    if (password !== confirmPassword) {
      setError("Heslá sa nezhodujú");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          clinicName: clinicName.trim(),
          subscribeNewsletter: newsletterSubscribed,
          discountCode: discountCode.trim() || null,
          isProvider,
          plan,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.code || "REGISTRATION_FAILED");
      }

      // Registration successful - now log in the user
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (loginError) {
        throw new Error(
          `Registration successful but login failed: ${loginError.message}`
        );
      }

      // Track the Google Ads conversion (registration completed successfully).
      trackRegistrationConversion();

      // Clear form
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setClinicName("");
      setFirstName("");
      setLastName("");
      setTermsChecked(false);
      setNewsletterSubscribed(false);
      setIsProvider(true);
      setPlan("solo");
      setDiscountCode("");
      setDiscountCodeStatus(null);

      // Redirect to admin homepage
      router.push("/admin/onboarding");
    } catch (error) {
      console.error("Registration error:", error);
      const code =
        error instanceof Error ? error.message : "REGISTRATION_FAILED";

      const errorMessages: Record<string, string> = {
        USER_ALREADY_EXISTS: "Používateľ s týmto emailom už existuje",
        CLINIC_ALREADY_EXISTS: "Klinika s týmto názvom už existuje",
        INVALID_EMAIL: "Zadaný email nie je platný",
        PASSWORD_TOO_SHORT: "Heslo musí mať aspoň 8 znakov",
        MISSING_FIELDS: "Vyplňte všetky povinné polia",
      };

      setError(
        errorMessages[code] ?? "Chyba pri registrácii. Skúste to znova."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{ width: "100%", minHeight: "100vh", backgroundColor: "#fff" }}
      className="shadow-border"
    >
      <div className="container">
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            backgroundImage: 'url("/images/registration-bg.jpeg")',
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.3,
            zIndex: 0,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        />

        {/* On the photograph, top left. It used to sit in a white band above
            it, which cost 100px of the image for no reason. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/ordinex-logo.png"
          alt="Ordinex"
          style={{
            position: "absolute",
            top: 24,
            left: 24,
            width: 200,
            zIndex: 2,
          }}
        />
        <div
          style={{
            width: 500,
            padding: 20,
            position: "relative",
            zIndex: 1,
            margin: 20,
          }}
          className="shadow-border card"
        >
          <h2 style={{ textAlign: "center", marginBottom: 28 }}>Registrácia</h2>
          {error && (
            <div
              style={{
                backgroundColor: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: "0.375rem",
                padding: "0.75rem",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              <div style={{ color: "#B91C1C", fontSize: "0.875rem" }}>
                {error}
              </div>
            </div>
          )}
          <div style={fieldStyle}>
            <label htmlFor="email" style={labelStyle}>
              Názov kliniky / ambulancie
            </label>
            <input
              value={clinicName}
              onChange={(e) => setClinicName(e.target.value)}
              type="text"
              id="clinicName"
              name="clinicName"
              data-testid="clinic-name-input"
              placeholder="Zadajte meno kliniky"
              style={{ backgroundColor: "#F3F4F6FF" }}
              disabled={loading}
            />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldStyle}>
              <label htmlFor="name" style={labelStyle}>
                Meno
              </label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                type="text"
                id="name"
                name="name"
                data-testid="first-name-input"
                placeholder="Zadajte meno"
                style={{ backgroundColor: "#F3F4F6FF" }}
                disabled={loading}
              />
            </div>
            <div style={fieldStyle}>
              <label htmlFor="lastName" style={labelStyle}>
                Priezvisko
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                type="text"
                id="lastName"
                name="lastName"
                data-testid="last-name-input"
                placeholder="Zadajte priezvisko"
                style={{ backgroundColor: "#F3F4F6FF" }}
                disabled={loading}
              />
            </div>
          </div>
          <div style={{ marginTop: 20, marginBottom: 20 }}>
            <label style={labelStyle}>Plán</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 8,
                marginTop: 8,
              }}
            >
              {SELECTABLE_PLANS.map((option) => (
                <label
                  key={option.key}
                  data-testid={`plan-${option.key}`}
                  style={{
                    display: "block",
                    border:
                      plan === option.key
                        ? "1.5px solid #5B9BD5"
                        : "1px solid #E1E1E1",
                    background: plan === option.key ? "#F5F9FE" : "#fff",
                    borderRadius: 8,
                    padding: 12,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="radio"
                      name="plan"
                      checked={plan === option.key}
                      onChange={() => setPlan(option.key as BillablePlan)}
                      disabled={loading}
                    />
                    <span style={{ fontWeight: 600, color: "#2D3436" }}>
                      {option.name}
                    </span>
                    <span style={{ color: "#636E72", fontSize: 13 }}>
                      {option.monthly} € / mesiac
                    </span>
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#636E72",
                      lineHeight: 1.5,
                      marginTop: 6,
                    }}
                  >
                    {option.description}
                  </span>
                </label>
              ))}
            </div>
            <p
              style={{
                fontSize: 13,
                color: "#636E72",
                margin: "8px 0 0 0",
                lineHeight: 1.5,
              }}
            >
              Počas skúšobného obdobia neplatíte nič a plán môžete kedykoľvek
              zmeniť v sekcii Platby.
            </p>
          </div>

          {/* Solo is one person, who must also be the provider — there is
              nothing to choose. The question only means something on Plus. */}
          {plan === "plus" && (
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <label style={labelStyle}>Vaša rola v ambulancii</label>
              <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                {[
                  {
                    value: true,
                    label: "Správca a poskytovateľ",
                    description:
                      "Spravujete ambulanciu a zároveň prijímate pacientov na vlastný rozvrh.",
                    testId: "role-admin-provider",
                  },
                  {
                    value: false,
                    label: "Správca",
                    description:
                      "Spravujete ambulanciu, ale nemáte vlastný rozvrh. Poskytovateľov pozvete neskôr.",
                    testId: "role-admin-only",
                  },
                ].map((option) => (
                  <label
                    key={String(option.value)}
                    data-testid={option.testId}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      border:
                        isProvider === option.value
                          ? "1.5px solid #5B9BD5"
                          : "1px solid #E1E1E1",
                      background:
                        isProvider === option.value ? "#F5F9FE" : "#fff",
                      borderRadius: 8,
                      padding: 12,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="registrantRole"
                      checked={isProvider === option.value}
                      onChange={() => setIsProvider(option.value)}
                      disabled={loading}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ fontWeight: 600, color: "#2D3436" }}>
                        {option.label}
                      </span>
                      <span
                        style={{
                          display: "block",
                          fontSize: 13,
                          color: "#636E72",
                          lineHeight: 1.5,
                        }}
                      >
                        {option.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={fieldStyle}>
            <label htmlFor="email" style={labelStyle}>
              Email
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              id="email"
              name="email"
              data-testid="email-input"
              placeholder="Zadajte email"
              style={{ backgroundColor: "#F3F4F6FF" }}
              disabled={loading}
            />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldStyle}>
              <label htmlFor="password" style={labelStyle}>
                Heslo
              </label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                id="password"
                name="password"
                data-testid="password-input"
                placeholder="Zadajte heslo"
                style={{ backgroundColor: "#F3F4F6FF" }}
                disabled={loading}
              />
            </div>
            <div style={fieldStyle}>
              <label htmlFor="confirmPassword" style={labelStyle}>
                Potvrďte heslo
              </label>
              <input
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                data-testid="confirm-password-input"
                placeholder="Zadajte heslo znova"
                style={{ backgroundColor: "#F3F4F6FF" }}
                disabled={loading}
              />
            </div>
          </div>
          {/* Optional, and most people have no code — a permanent field for it
              costs every registrant a row. The link is still visible, so
              anyone holding a code can find it. */}
          {!showDiscountCode && (
            <button
              type="button"
              onClick={() => setShowDiscountCode(true)}
              data-testid="discount-code-toggle"
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginBottom: 16,
                color: "#5B9BD5",
                fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                textAlign: "left",
              }}
            >
              Mám zľavový kód
            </button>
          )}

          <div style={showDiscountCode ? fieldStyle : { display: "none" }}>
            <label htmlFor="discountCode" style={labelStyle}>
              Zľavový kód{" "}
              <span style={{ fontWeight: 400, color: "#6B7280" }}>
                (nepovinné)
              </span>
            </label>
            <input
              value={discountCode}
              onChange={(e) => handleDiscountCodeChange(e.target.value)}
              type="text"
              id="discountCode"
              name="discountCode"
              data-testid="discount-code-input"
              placeholder="Zadajte zľavový kód"
              style={{ backgroundColor: "#F3F4F6FF" }}
              disabled={loading}
            />
            {discountCodeStatus === "invalid" && (
              <div style={{ color: "#B91C1C", fontSize: "0.8rem" }}>
                Neplatný kód
              </div>
            )}
            {discountCodeStatus === "valid" && (
              <div
                style={{
                  backgroundColor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  borderRadius: "0.375rem",
                  padding: "0.6rem 0.75rem",
                  color: "#15803D",
                  fontSize: "0.8rem",
                }}
              >
                ✓ Kód bol úspešne uplatnený – získavate 2 mesiace zadarmo!
              </div>
            )}
          </div>

          <Button
            variant="primary"
            style={{
              width: "100%",
              justifyContent: "center",
              marginTop: 20,
              marginBottom: 10,
            }}
            disabled={loading}
            data-testid="register-button"
            onClick={registrationHandler}
          >
            {loading ? "Registruje sa..." : "Zaregistrovať sa"}
          </Button>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 5,
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              id="terms"
              name="terms"
              checked={termsChecked}
              onChange={(e) => setTermsChecked(e.target.checked)}
              style={{ marginTop: 2 }}
              data-testid="terms-checkbox"
            />
            <label htmlFor="terms">
              Súhlasím so{" "}
              <a
                href="https://ordinex.sk/obchodne-podmienky"
                target="_blank"
                className="link"
              >
                Všeobecnými obchodnými podmienkami
              </a>{" "}
              a{" "}
              <a
                href="https://ordinex.sk/ochrana-osobnych-udajov"
                target="_blank"
                className="link"
              >
                Zásadami ochrany osobných údajov Ordinexu.
              </a>
            </label>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 5,
              fontSize: 13,
            }}
          >
            <input
              style={{ marginTop: 2 }}
              type="checkbox"
              id="newsletter"
              name="newsletter"
              checked={newsletterSubscribed}
              onChange={(e) => setNewsletterSubscribed(e.target.checked)}
            />
            <label htmlFor="newsletter">Newsletter od Ordinex </label>
          </div>
        </div>
      </div>
      <style>{`
      .container {
        position: relative;
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      /* Shift to the right on large screens */
      @media (min-width: 1024px) {
        .container {
          // justify-content: flex-end;  /* stick to the right */
          // padding-right: 6vw;         /* breathing room from edge */
          padding-left: 450px;
        }
      }
    `}</style>
    </div>
  );
}
